// HoldingsViz — the Holdings card's chart: a one-line header over a fixed-height stage
// that swaps the reusable BarChart and Treemap. Owns the view mode. Source of truth:
// portfolio3.jsx .pf3-hero-viz, which feeds both charts ITEMS — the TOP LEVEL of
// the holdings list. A managed model is therefore ONE solid bar / tile, not several
// bars that would double-count its balance; clicking it opens the model allocation
// modal (`onPickModel`), which draws the member funds as their own chart.
//
// THE HEADER reads left to right as one line: what you're looking at ("8 funds ·
// Sorted by balance"), then the YTD figure, then that figure broken out into a pill per
// source (You / employer match / market). It replaces the separate stat card that used
// to sit under the chart: the figure and the chart describe the same money, and split
// across two rows the header line was mostly empty while the card below was a wide box
// holding three short rows.
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { BarChart, Treemap } from '../../../reusable';
import { cx } from '../../../../utils/cx';
import { formatCurrency, formatSignedCurrency } from '../../../../utils/format';
import { countLabel, shareText } from '../portfolio.adapters';
import { FundTip } from './FundTip';
import { VizModeToggle } from './VizModeToggle';
import { niceAxis, fmtTick } from './vizScale';
import './holdings-viz.css';

// YTD-growth source dot colors, keyed by source (presentation, not data). Moved here with
// the breakdown itself — the header pills are the only thing that renders them now.
const SRC_COLORS = {
  you: 'var(--brand-blue)',
  match: 'var(--brand-green)',
  market: 'var(--pf-c-market)',
  // Credited-rate plans: the money arrives as an employer pay credit plus the
  // guaranteed interest on it, so the two read as one blue family rather than
  // borrowing the you/match/market palette, which would imply the participant
  // contributed some of it. (The design's mid-navy #1B4283 has no token yet;
  // brand blue is the nearest one and stays in family.)
  paycredit: 'var(--pf-navy-deep)',
  interest: 'var(--brand-blue)',
};

// A held sliver's tile is floored so it still draws; all floors together add at most
// MAX_TILE_PAD_PCT of the map, so many slivers can't squeeze the large tiles (FM-3084).
const MIN_TILE_PCT = 1.5;
const MAX_TILE_PAD_PCT = 5;

const pctOf = (h) => (h.balancePct != null ? h.balancePct : h.pct);
// Shares arrive rounded to 0.01, so a held fund under 0.005% reads 0; held is decided by balance.
const isHeld = (h) => h.balance > 0;

export function HoldingsViz({
  funds = [], items, plan, onPickFund, onPickModel, ytdGrowth, credited = false,
  // Which view the card opens on. Defaults to 'bar' so the Portfolio page keeps the
  // behaviour it has always had; the combined dashboard passes 'map' because the
  // approved FM-2406 design shows the treemap. The toggle still works either way.
  defaultMode = 'bar',
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(defaultMode); // 'bar' | 'map'

  // The holdings list when we have one (models aggregated), else the flat funds.
  const holdings = useMemo(
    () => (items && items.length ? items : funds.map((f) => ({ ...f, type: 'fund', key: f.ticker }))),
    [items, funds],
  );

  const byKey = useMemo(
    () => Object.fromEntries(holdings.map((h) => [h.key ?? h.ticker, h])),
    [holdings],
  );

  const { max, ticks } = useMemo(
    () => niceAxis(Math.max(0, ...holdings.map((h) => h.balance))),
    [holdings],
  );

  // A model has no ticker of its own on some payloads — fall back to a short name
  // so the axis never renders a blank bar label.
  const shortLabel = (h) =>
    h.ticker || (h.type === 'model' ? h.name.replace(/\s*Model$/i, '') : h.key);

  // A managed model is ONE solid bar with a "model · N funds" line under its tick. It
  // used to be segmented per member fund, but a 100-fund model made every segment an
  // unclickable sliver — so the whole bar is the target and the member funds get their
  // own chart in the model allocation modal.
  const fundCount = (h) => (h.type === 'model' && h.children ? h.children.length : 0);
  const barItems = useMemo(
    () =>
      holdings.map((h) => ({
        key: h.key ?? h.ticker,
        label: shortLabel(h),
        value: h.balance,
        topLabel: shareText(h),
        color: h.color,
        subLabel: fundCount(h) ? t('portfolio.viz.modelSub', { count: fundCount(h) }) : undefined,
      })),
    [holdings, t],
  );

  // The tile's second line says what it is: a fund shows its balance, a model its fund
  // count, so the two read differently before any hover.
  const mapItems = useMemo(() => {
    const pad = (h) => (isHeld(h) ? Math.max(MIN_TILE_PCT - pctOf(h), 0) : 0);
    const totalPad = holdings.reduce((s, h) => s + pad(h), 0);
    const scale = totalPad > MAX_TILE_PAD_PCT ? MAX_TILE_PAD_PCT / totalPad : 1;
    return holdings.map((h) => ({
      key: h.key ?? h.ticker,
      // The label keeps the real %; only the area is floored (FM-3084).
      value: isHeld(h) ? pctOf(h) + pad(h) * scale : 0,
      pctLabel: shareText(h),
      color: h.color,
      label: shortLabel(h),
      name: h.name.replace('Vanguard ', ''),
      sub: fundCount(h) ? t('portfolio.viz.modelSub', { count: fundCount(h) }) : formatCurrency(h.balance),
    }));
  }, [holdings, t]);

  // A model bar/tile opens the model allocation modal; a fund opens its fact sheet.
  const pick = (item) => {
    const h = byKey[item.key];
    if (!h) return;
    if (h.type === 'model') onPickModel?.(h.key);
    else onPickFund?.(h.ticker);
  };

  // The tip REPORTS; the shape under it is the control. Its footer note says so —
  // "Click on bar to view fund fact sheet" — and `surface` is which chart is showing,
  // so the note names the shape the participant is actually pointing at ("tile" on
  // the treemap). Nothing in the card is clickable, which is why neither chart passes
  // `interactiveTip` any more: a card that parks and becomes hit-testable would shield
  // the very bar the note sends you to. A model's footer says the click opens its funds.
  const renderTip = (item) => (
    <FundTip
      fund={byKey[item.key]}
      plan={plan}
      surface={mode === 'bar' ? 'bar' : 'tile'}
    />
  );

  const ytdTotal = ytdGrowth?.total;
  const sources = ytdGrowth?.breakdown || [];

  return (
    <div className="holdings-viz">
      <div className="holdings-viz__head">
        <div className="holdings-viz__meta">
          {/* Two spans, not one string: the count and the mode title are separate facts
              (and separately translated), joined by a divider glyph the reader doesn't
              need announced. */}
          <span className="holdings-viz__what">
            <span>{countLabel(holdings, t)}</span>
            <span className="holdings-viz__sep" aria-hidden="true">·</span>
            <span className="holdings-viz__title">
              {mode === 'bar' ? t('portfolio.viz.sortedByBalance') : t('portfolio.viz.sizedByBalance')}
            </span>
          </span>

          {/* The YTD figure. Omitted outright where the plan reports none — a bare "—"
              beside the fund count states nothing. `null` must be tested BEFORE the sign
              comparison: JS coerces it to 0, so a missing total would read as a gain. */}
          {ytdTotal != null && (
            <span className="holdings-viz__ytd">
              <span className="holdings-viz__ytd-lbl">
                {t(credited ? 'portfolio.overview.ytdAccountChange' : 'portfolio.overview.ytdGrowth')}
              </span>
              <strong className={cx('holdings-viz__ytd-val', ytdTotal >= 0 ? 'is-pos' : 'is-neg')}>
                {formatSignedCurrency(ytdTotal, false)}
              </strong>
            </span>
          )}

          {/* …and where it came from, a pill per source. Labels are data (the plan names
              its own employer match), so they are printed, not translated.

              The wrapper is `display: contents` at every width but the phone one, so
              the pills stay direct children of this row and the header is unchanged;
              on a phone it becomes a real box, which is what lets the pills drop to
              their own line under the figure-and-toggle row. */}
          <span className="holdings-viz__srcs">
            {sources.map((b) => (
              <span key={b.key} className="holdings-viz__src">
                <i
                  className="holdings-viz__src-dot"
                  style={{ background: SRC_COLORS[b.key] || 'var(--text-3)' }}
                />
                <span className="holdings-viz__src-lbl">{b.label}</span>
                <strong className="holdings-viz__src-val">{formatCurrency(b.amount, false)}</strong>
              </span>
            ))}
          </span>
        </div>
        <VizModeToggle className="holdings-viz__toggle" mode={mode} onChange={setMode} />
      </div>

      <div className="holdings-viz__stage">
        {mode === 'bar' ? (
          <BarChart
            items={barItems}
            maxValue={max}
            ticks={ticks}
            formatTick={fmtTick}
            onSelect={pick}
            renderTip={renderTip}
            ariaLabel={t('portfolio.viz.barChartAria')}
          />
        ) : (
          <Treemap
            items={mapItems}
            onSelect={pick}
            renderTip={renderTip}
            ariaLabel={t('portfolio.viz.treemapAria')}
          />
        )}
      </div>
    </div>
  );
}

HoldingsViz.propTypes = {
  /** Flat investable funds — the fallback when no holdings list is supplied. */
  funds: PropTypes.array,
  /** Top-level holdings (models aggregated) — what both charts plot. */
  items: PropTypes.array,
  /** Plan record — the tooltip's Future-election row asks it whether elections apply. */
  plan: PropTypes.object,
  onPickFund: PropTypes.func,
  /** (modelKey) — a model bar/tile was clicked; opens the model allocation modal. */
  onPickModel: PropTypes.func,
  /** Which view the card opens on: 'bar' (Portfolio) or 'map' (combined dashboard). */
  defaultMode: PropTypes.oneOf(['bar', 'map']),
  /** YTD growth (adapter: `holdings.ytdGrowth`) — the header figure + its source pills. */
  ytdGrowth: PropTypes.shape({
    total: PropTypes.number,
    breakdown: PropTypes.arrayOf(
      PropTypes.shape({ key: PropTypes.string, label: PropTypes.string, amount: PropTypes.number }),
    ),
  }),
  /** Credited-rate plan → the figure is an account change, not growth earned. */
  credited: PropTypes.bool,
};

export default HoldingsViz;
